(ns splash.ui.lender-portal.views.pages.documents
  (:require
    ["@azure/msal-react" :refer [useMsal]]
    ["react-router-dom" :refer [useParams]]
    [react :as react]
    [reagent-mui.icons.open-in-new-outlined :refer [open-in-new-outlined]]
    [reagent.core :as r]
    [splash.ui.lender-portal.common.auth :as auth]
    [splash.ui.lender-portal.common.request :as request]
    [splash.ui.lender-portal.common.util :as util]
    [splash.ui.lender-portal.components.application-banner :refer [application-banner]]
    [splash.ui.lender-portal.components.layout :as layout]
    [splash.ui.lender-portal.components.mui :as mui]
    [splash.ui.lender-portal.components.nav-supporting-pages :refer [supporting-pages]]
    [splash.ui.lender-portal.components.search-box :refer [search-box]]
    [splash.ui.lender-portal.components.section :refer [section]]
    [splash.ui.lender-portal.datadog :as datadog]
    [splash.ui.lender-portal.state :as state]))

(def current-document (r/atom nil))

(def application-documents-subtypes
  #{"Adverse Action Notice"
    "Application Summary"
    "Application and Solicitation Disclosure"
    "Approval Disclosure"
    "Auto Debit Agreement"
    "Bethpage Disclosures"
    "Borrower E-Signed Documents"
    "Credit Agreement"
    "Credit Agreement Signing Certificate"
    "Counteroffer Adverse Action Notice"
    "Credit Report"
    "Debt To Income Summary"
    "ELFI Adverse Action Notice"
    "Final Disclosure"
    "First Tech Mebership Agreement"
    "First Tech Mebership Agreement Signing Certificate"
    "Lexis Nexis Fraud Report"
    "LexisNexis InstantID"
    "Loan Documents"
    "Membership Agreement"
    "Membership Agreement Signing Certificate"
    "Nelnet Bank Uniform Disclosure"
    "PenFed Membership"
    "Risk Based Pricing Notice"
    "Socure Back of Drivers License"
    "Socure Selfie"
    "Socure Summary"
    "Teachers Federal Membership Agreement"
    "Teachers Federal Membership Agreement Signing Certificate"})

(defn application-document?
  [{:keys [document-subtype]}]
  (contains? application-documents-subtypes document-subtype))

(def default-column-opts
  {:sortable false})

(defn documents-table-columns
  [include-status-column?]
  (let [fields [{:field "document-type"
                 :headerName "Type"
                 :flex 1}
                {:field "document-subtype"
                 :headerName "Subtype"
                 :flex 1}
                {:field "created-at"
                 :headerName "Created At"
                 :width 165
                 :valueGetter (fn [params]
                                (when (string? (.-value params))
                                  (-> (.-value params)
                                    (util/datetime-zero-ms-string->datetime)
                                    (util/->to-local)
                                    (util/datetime->datetime-string))))}]]
    (cond-> (map #(merge default-column-opts %) fields)
      include-status-column? (concat [{:field "status"
                                       :headerName "Status"
                                       :width 100
                                       :valueGetter (fn [params]
                                                      (if (= "New" (.-value params))
                                                        "Unverified"
                                                        (.-value params)))}]))))

(defn documents-table
  [{:keys [state-key rows on-row-model-change row-state include-status-column?]}]
  (let [[row-model _] row-state]
    [mui/paper
     [mui/data-grid {:columns (documents-table-columns include-status-column?)
                     :rows rows
                     :hideFooter true
                     :autoHeight true
                     :rowHeight 32
                     :rowSelectionModel row-model
                     :onRowClick (fn [params _ _]
                                   (reset! current-document (.-row params)))
                     ;; wrap the handler from the parent
                     ;; with this table's key, so we know
                     ;; which table triggered the update
                     :onRowSelectionModelChange (partial on-row-model-change state-key)}]]))

(defn documents-table-section
  [{:keys [title] :as table-def}]
  [section title
   [documents-table table-def]])

;; These two tables use controlled state
;; as we can only display one document at a time.
;; The on-row-model-change function is responsible
;; for setting the row-model only for the table
;; that triggered the change and clearing the rest.
;; This is done by maintaining row model state with separate hooks
;; and updating the row model state for all tables on any change.
;; Without this functionality, both tables can have a row selected
;; at the same time which would result in a confusing UX where
;; both tables show a selected row but only the recently 
;; selected document would be displayed.
(defn documents-tables
  [documents show-all-documents?]
  ;; each table needs it's own state hook 
  ;; to maintain the selected rows
  (let [borrower-documents (cond->> (filter (comp not application-document?) documents)
                             (not show-all-documents?) (filter util/is-approved-document?))
        application-documents (filter application-document? documents)
        row-states {:borrower (react/useState [])
                    :application (react/useState [])}
        ;; when one tables row model changes (a row is selected),
        ;; update that table's model and clear the other(s)
        on-row-model-change (fn [triggered-state-key new-model]
                              (doseq [[state-key [_ set-model]] row-states]
                                (set-model (if (= state-key triggered-state-key)
                                             new-model
                                             []))))]
    [mui/stack {:spacing 4}
     [documents-table-section {:state-key :borrower
                               :title "Borrower Originated Documents"
                               :include-status-column? true
                               :rows borrower-documents
                               :on-row-model-change on-row-model-change
                               :row-state (:borrower row-states)}]
     [documents-table-section {:state-key :application
                               :title "Application Documents"
                               :rows application-documents
                               :on-row-model-change on-row-model-change
                               :row-state (:application row-states)}]]))

(defn open-document-viewer
  [application-id document]
  (let [document-id (get document "id")]
    (.open js/window (str "/application/" application-id "/documents/" document-id)
      (str "document-viewer-" document-id)
      "width=900,height=800,top=0,scrollbars=no,toolbar=no,menubar=no")))

(defn document-viewer
  [application-id]
  (let [document  (js->clj @current-document)
        file-type (util/get-file-type (get document "original-filename") (get document "mime-type"))]
    [mui/paper {:sx {:padding   "8px 12px"
                     :height    "90vh"
                     :overflow  (if (= :image file-type) "scroll" "hidden")}}
     (when document
       (let [url (get document "url")]
         [:<>
          [mui/box {:sx {:display         "flex"
                         :justify-content "flex-end"
                         :align-items     "center"}}
           [mui/icon-button {:size    "large"
                             :onClick (fn [] (open-document-viewer application-id document))}
            [r/as-element [open-in-new-outlined]]]]
          (if (= :image file-type)
            [:img {:class "dd-privacy-mask"
                   :src   url
                   :style {:maxWidth "100%"}}]
            [:embed {:class "dd-privacy-mask"
                     :src    url
                     :width  "100%"
                     :height "100%"}])]))]))

(defn find-matching-document
  [website-document application]
  (let [document-id (get website-document "id")]
    (->> (:documents application)
      (filter #(= (:document-id %) document-id))
      (first))))

(defn document-comment
  []
  (let [state (state/use-state)
        current-application (r/cursor state [:current-application])
        application (js->clj @current-application)
        website-document (js->clj @current-document)
        matching-document (find-matching-document website-document application)
        document-comment (:document-comment matching-document)]
    (when document-comment
      [section "Document Comment"
       [mui/paper {:sx {:padding "24px"}}
        ;; will add this back once available
        #_[mui/typography {:variant "caption"
                         :display "block"}
         "Marco Fatica | 08/05/2023"]
        [mui/typography {:variant "body1"}
         document-comment]]])))

(defn documents-detail
  [current-application]
  (let [params (useParams)
        application-id (-> ^js/String (.-applicationId params)
                         parse-long)]
    (when (= application-id (:application-id @current-application))
      [mui/box {:sx {:width "100%"
                     :padding "16px"}}
       [mui/grid {:container true
                  :spacing 2}
        [mui/grid {:item true
                   :xs 12
                   :lg 5}
         (let [show-all-documents? (-> (:success-status @current-application)
                                     (= "Denied"))]
           [documents-tables (:website-documents @current-application) show-all-documents?])]
        [mui/grid {:item true
                   :xs 12
                   :lg 7}
         [mui/stack {:spacing 4}
          [document-viewer application-id]
          [document-comment]]]]])))

(defn sidebar
  []
  [mui/stack
   [search-box]
   [supporting-pages]])

(defn view
  []
  (r/with-let [msal-instance (.-instance (useMsal))
               state (state/use-state)
               loading (r/cursor state [:loading])
               current-application (r/cursor state [:current-application])
               params (useParams)
               application-id (-> ^js/String (.-applicationId params)
                                parse-long)
               handle-result (fn [result]
                               (reset! loading false)
                               (reset! current-application (js->clj result)))
               mk-get-application-request (fn [application-id token]
                                            (request/mk-json-request
                                              {:uri (str "/api/v1/applications/" application-id)
                                               :token token
                                               :method :get
                                               :handler handle-result
                                               :error-handler (fn [e]
                                                                (datadog/report-error
                                                                  (str "Failed to pull documents for application " application-id)
                                                                  e
                                                                  {:application-id application-id})
                                                                (handle-result nil))}))
               on-load (fn []
                         (reset! current-document nil)
                         (when (not (= application-id (:application-id @current-application)))
                           (reset! loading true)
                           (-> (auth/acquire-token msal-instance)
                             (.then (partial mk-get-application-request application-id)))))]
    (r/create-class
      {:component-did-mount on-load
       :reagent-render
       (fn []
         [layout/default
          (if (= application-id (:application-id @current-application))
            [:<>
             [application-banner current-application]
             [layout/container
              [sidebar]
              [documents-detail current-application]]]
            [mui/backdrop {:open @loading
                           ;; overlay should cover everything,
                           ;; but for some reason DataGrids are 
                           ;; rendering over the backdrop
                           :sx {:z-index 999}}
             [mui/circular-progress]])])})))
